import { createFlashMessage } from '@components/Flash/redux';
import {
  deleteClassGroup,
  deleteFAQ,
  exportConfirmedList,
  exportReprovedAndDropoutList,
  exportWaitingList,
  exportWaitingToConfirmList,
  getAllClassGroups,
  getAllFAQs,
  getClassGroupUsers,
  getClassGroupsByGroup,
  getConfirmedList,
  getEnrollmentAuditLogs,
  getGroups,
  getPreEnrolled,
  getPublicFAQs,
  getReprovedAndDropoutList,
  getUsers,
  getWaitingList,
  getWaitingToConfirmList,
  postCreateFAQ,
  postNewClassGroup,
  putEditClassGroup,
  putEditUser,
  putEnrollStudent,
  putMarkDropout,
  putMoveUsersBetweenClassGroup,
  putUpdateFAQ,
  putUpdateFAQOrder
} from '@utils/api/admin';
import { format, isValid } from 'date-fns';
import { navigate as gatsbyNavigate } from 'gatsby';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { actionTypes } from './action-types';
import { startLoading, stopLoading } from './actions';

function* fetchAllUsers({ payload }) {
  try {
    const filter = {
      filter: payload
    };

    if (!filter.filter) {
      filter.filter = {
        pageNumber: 1,
        pageSize: 5,
        sort: 'startAt',
        order: 'DESC'
      };
    }

    const { result, success, message } = yield call(getUsers, filter);
    if (success) {
      yield put({
        type: actionTypes.fetchAllUsersComplete,
        payload: result
      });
    } else {
      yield put(
        createFlashMessage({
          type: 'danger',
          message,
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Error fetching all users:', e);
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Indisponível, tente novamente em alguns minutos.',
        variables: { dismissible: true }
      })
    );
  }
}

function* fetchAllPreEnrolled({ payload }) {
  try {
    const filter = {
      filter: payload
    };

    if (!filter.filter) {
      filter.filter = {
        pageNumber: 1,
        pageSize: 5,
        sort: 'startAt',
        order: 'DESC'
      };
    }

    const createdAtRangeDate = filter.filter.createdAtRangeDate ?? null;

    if (
      createdAtRangeDate?.[0] &&
      createdAtRangeDate?.[1] &&
      isValid(new Date(createdAtRangeDate[0])) &&
      isValid(new Date(createdAtRangeDate[1]))
    ) {
      const firstDate = format(createdAtRangeDate[0], 'yyyy-MM-dd');
      const secondDate = format(createdAtRangeDate[1], 'yyyy-MM-dd');

      filter.filter.createdAtStartDate = firstDate;
      filter.filter.createdAtEndDate = secondDate;
    }
    delete filter.filter.createdAtRangeDate;

    const { result, success, message } = yield call(getPreEnrolled, filter);
    if (success) {
      yield put({
        type: actionTypes.fetchAllPreEnrolledComplete,
        payload: result
      });
    } else {
      yield put(
        createFlashMessage({
          type: 'danger',
          message,
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Erro no Saga:', e);
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Indisponível, tente novamente em alguns minutos.',
        variables: { dismissible: true }
      })
    );
  }
}

function* fetchEditUser({ payload }) {
  try {
    const response = yield call(putEditUser, payload);
    if (response.success) {
      yield put(
        createFlashMessage({
          type: 'success',
          message: response.message,
          variables: { dismissible: true }
        })
      );
      yield put({
        type: actionTypes.fetchAllUsers,
        payload: payload.filter || {}
      });
      yield put({ type: actionTypes.closeEditUserDrawer });
    } else {
      yield put(
        createFlashMessage({
          type: 'danger',
          message: response.message || 'Erro ao editar usuário.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Erro no Saga:', e);
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Ocorreu um erro inesperado. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  } finally {
    yield put({ type: actionTypes.fetchEditUserComplete });
  }
}

function* fetchAllClassGroups({ payload }) {
  try {
    const filter = {
      filter: payload
    };

    if (!filter.filter) {
      filter.filter = {
        pageNumber: 1,
        pageSize: 5,
        sort: 'startAt',
        order: 'DESC'
      };
    }

    if (filter.filter.status && Array.isArray(filter.filter.status)) {
      filter.filter.status = JSON.stringify(filter.filter.status);
    }
    if (filter.filter.group && Array.isArray(filter.filter.group)) {
      filter.filter.group = JSON.stringify(filter.filter.group);
    }

    const { result, success, message } = yield call(getAllClassGroups, filter);
    if (success) {
      yield put({
        type: actionTypes.fetchAllClassGroupsComplete,
        payload: result
      });
    } else {
      yield put(
        createFlashMessage({
          type: 'danger',
          message,
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Error fetching all classGroups:', e);
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Indisponível, tente novamente em alguns minutos.',
        variables: { dismissible: true }
      })
    );
  }
}

function* fetchClassGroupUsers({ payload }) {
  try {
    const { classGroupId, params } = payload;

    const { result, success, message } = yield call(
      getClassGroupUsers,
      classGroupId,
      params
    );

    if (success) {
      yield put({
        type: actionTypes.fetchClassGroupUsersComplete,
        payload: result
      });
    } else {
      yield put({
        type: actionTypes.fetchClassGroupUsersError,
        payload: { error: message }
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message: message || 'Erro ao buscar usuários da turma.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Error fetching class group users:', e);
    yield put({
      type: actionTypes.fetchClassGroupUsersError,
      payload: { error: e.message }
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Erro ao buscar usuários da turma.',
        variables: { dismissible: true }
      })
    );
  }
}

function* newClassGroup({ payload }) {
  try {
    if (payload.curriculumAvailable.length === 0) {
      return yield put(
        createFlashMessage({
          type: 'danger',
          message:
            'É preciso escolher pelo menos um item do conteúdo associado para cadastrar uma turma',
          variables: { dismissible: true }
        })
      );
    }
    const response = yield call(postNewClassGroup, payload);
    if (response.success) {
      yield put(
        createFlashMessage({
          type: 'success',
          message: response.message,
          variables: { dismissible: true }
        })
      );
      yield gatsbyNavigate('/admin/class-groups/');
    } else {
      yield put(
        createFlashMessage({
          type: 'danger',
          message: response.message || 'Erro ao editar usuário.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Erro no Saga:', e);
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Ocorreu um erro inesperado. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  }
}

function* editClassGroup({ payload }) {
  try {
    if (payload.curriculumAvailable.length === 0) {
      yield put({
        type: actionTypes.editClassGroupError
      });
      return yield put(
        createFlashMessage({
          type: 'danger',
          message:
            'É preciso escolher pelo menos um item do conteúdo associado para editar uma turma',
          variables: { dismissible: true }
        })
      );
    }
    const response = yield call(putEditClassGroup, payload);
    if (response.success) {
      yield put({
        type: actionTypes.editClassGroupComplete
      });
      yield put(
        createFlashMessage({
          type: response?.warning ? 'warning' : 'success',
          message: response.message,
          variables: { dismissible: true }
        })
      );
      yield gatsbyNavigate('/admin/class-groups/');
    } else {
      yield put({
        type: actionTypes.editClassGroupError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message: response.message || 'Erro ao editar usuário.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Erro no Saga:', e);
    yield put({
      type: actionTypes.editClassGroupError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Ocorreu um erro inesperado. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  }
}

function* removeClassGroup({ payload }) {
  try {
    const response = yield call(deleteClassGroup, payload);
    if (response.success) {
      yield put(
        createFlashMessage({
          type: 'success',
          message: response.message,
          variables: { dismissible: true }
        })
      );
      yield gatsbyNavigate('/admin/class-groups/');
    } else {
      yield put(
        createFlashMessage({
          type: 'danger',
          message: response.message || 'Erro ao editar usuário.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Erro no Saga:', e);
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Ocorreu um erro inesperado. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  }
}

function* moveUsersBetweenClassGroups({ payload }) {
  try {
    if (payload.length === 0) {
      yield put(
        createFlashMessage({
          type: 'danger',
          message: 'É preciso adicionar usuários ao painel de destino!',
          variables: { dismissible: true }
        })
      );
    }
    const response = yield call(putMoveUsersBetweenClassGroup, payload);

    if (response.success) {
      yield put({
        type: actionTypes.moveUsersBetweenClassGroupsComplete
      });
      yield put(
        createFlashMessage({
          type: 'success',
          message: response.message || 'Usuários movidos com sucesso.',
          variables: { dismissible: true }
        })
      );
      yield gatsbyNavigate('/admin/class-groups/');
    } else {
      yield put({
        type: actionTypes.moveUsersBetweenClassGroupsError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message: response.message || 'Erro ao mover usuários.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Erro no Saga moveUsersBetweenGroups:', e);
    yield put({
      type: actionTypes.moveUsersBetweenClassGroupsError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message:
          'Ocorreu um erro inesperado ao mover usuários. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  }
}

function* createWaitingListSaga({ payload }) {
  try {
    const { result, success, message } = yield call(getWaitingList, payload);

    if (success) {
      yield put({
        type: actionTypes.fetchWaitingListComplete,
        payload: result
      });
    } else {
      yield put({
        type: actionTypes.fetchWaitingListError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message,
          variables: { dismissible: true }
        })
      );
    }
  } catch (error) {
    console.error('Error fetching waiting list:', error);
    yield put({
      type: actionTypes.fetchWaitingListError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Indisponível, tente novamente em alguns minutos.',
        variables: { dismissible: true }
      })
    );
  }
}

function* fetchExportWaitingListSaga({ payload }) {
  try {
    yield put(startLoading('exportReport'));
    const response = yield call(exportWaitingList, payload);

    if (!response.ok) throw new Error('Erro ao exportar planilha');

    const blob = yield response.blob();
    const formatedNow = format(new Date(), 'dd-MM-yyyy_HH-mm-ss');

    let group = 'geral';
    if (Array.isArray(payload.group) && payload.group.length > 0) {
      group = payload.group.map(g => g.replace(/\s+/g, '-')).join('_');
    }

    const fileName =
      `aguardando-vaga-${group}-${formatedNow}.xlsx`.toLocaleLowerCase();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error fetching waiting list:', error);
    yield put({
      type: actionTypes.fetchWaitingListError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Indisponível, tente novamente em alguns minutos.',
        variables: { dismissible: true }
      })
    );
  } finally {
    yield put(stopLoading('exportReport'));
  }
}

function* fetchReprovedAndDropoutListSaga({ payload }) {
  try {
    const { result, success, message } = yield call(
      getReprovedAndDropoutList,
      payload
    );

    if (success) {
      yield put({
        type: actionTypes.fetchReprovedAndDropoutListComplete,
        payload: result
      });
    } else {
      yield put({
        type: actionTypes.fetchReprovedAndDropoutListError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message,
          variables: { dismissible: true }
        })
      );
    }
  } catch (error) {
    console.error('Error fetching reproved and dropout list:', error);
    yield put({
      type: actionTypes.fetchReprovedAndDropoutListError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Indisponível, tente novamente em alguns minutos.',
        variables: { dismissible: true }
      })
    );
  }
}

function* fetchExportReprovedAndDropoutListSaga({ payload }) {
  try {
    yield put(startLoading('exportReport'));
    const response = yield call(exportReprovedAndDropoutList, payload);

    if (!response.ok) throw new Error('Erro ao exportar planilha');

    const blob = yield response.blob();
    const formatedNow = format(new Date(), 'dd-MM-yyyy_HH-mm-ss');

    let group = 'geral';
    if (Array.isArray(payload.group) && payload.group.length > 0) {
      group = payload.group.map(g => g.replace(/\s+/g, '-')).join('_');
    }

    let status = 'desistentes-reprovado';
    if (Array.isArray(payload.status) && payload.status.length > 0) {
      status = payload.status
        .map(s => s.toLowerCase().replace(/\s+/g, '-'))
        .join('_');
    }

    const fileName =
      `${status}s-${group}-${formatedNow}.xlsx`.toLocaleLowerCase();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error fetching reproved-and-dropout-export:', error);
    yield put({
      type: actionTypes.fetchWaitingListError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Indisponível, tente novamente em alguns minutos.',
        variables: { dismissible: true }
      })
    );
  } finally {
    yield put(stopLoading('exportReport'));
  }
}

function* fetchConfirmedListSaga({ payload }) {
  try {
    const { result, success, message } = yield call(getConfirmedList, payload);
    if (success) {
      yield put({
        type: actionTypes.fetchConfirmedListComplete,
        payload: result
      });
    } else {
      yield put({
        type: actionTypes.fetchConfirmedListError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message,
          variables: { dismissible: true }
        })
      );
    }
  } catch (error) {
    console.error('Error fetching confirmed list:', error);
    yield put({
      type: actionTypes.fetchConfirmedListError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Indisponível, tente novamente em alguns minutos.',
        variables: { dismissible: true }
      })
    );
  }
}

function* fetchExportConfirmedListSaga({ payload }) {
  try {
    yield put(startLoading('exportReport'));
    const response = yield call(exportConfirmedList, payload);

    if (!response.ok) throw new Error('Erro ao exportar planilha');

    const blob = yield response.blob();

    const formatedNow = format(new Date(), 'dd-MM-yyyy_HH-mm-ss');

    let groupOrSource = 'geral';
    if (Array.isArray(payload.group) && payload.group.length > 0) {
      groupOrSource = payload.group.map(g => g.replace(/\s+/g, '-')).join('_');
    } else if (Array.isArray(payload.source) && payload.source.length > 0) {
      groupOrSource = payload.source.map(g => g.replace(/\s+/g, '-')).join('_');
    }

    const fileName =
      `confirmados-${groupOrSource}-${formatedNow}.xlsx`.toLocaleLowerCase();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error fetching waiting list:', error);
    yield put({
      type: actionTypes.fetchWaitingListError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Indisponível, tente novamente em alguns minutos.',
        variables: { dismissible: true }
      })
    );
  } finally {
    yield put(stopLoading('exportReport'));
  }
}

function* fetchWaitingToConfirmListSaga({ payload }) {
  try {
    const { result, success, message } = yield call(
      getWaitingToConfirmList,
      payload
    );
    if (success) {
      yield put({
        type: actionTypes.fetchWaitingToConfirmListComplete,
        payload: result
      });
    } else {
      yield put({
        type: actionTypes.fetchWaitingToConfirmListError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message,
          variables: { dismissible: true }
        })
      );
    }
  } catch (error) {
    console.error('Error fetching confirmed list:', error);
    yield put({
      type: actionTypes.fetchWaitingToConfirmListError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Indisponível, tente novamente em alguns minutos.',
        variables: { dismissible: true }
      })
    );
  }
}

function* fetchExportWaitingToConfirmListSaga({ payload }) {
  try {
    yield put(startLoading('exportReport'));
    const response = yield call(exportWaitingToConfirmList, payload);

    if (!response.ok) throw new Error('Erro ao exportar planilha');

    const blob = yield response.blob();

    const formatedNow = format(new Date(), 'dd-MM-yyyy_HH-mm-ss');

    let groupOrSource = 'geral';
    if (Array.isArray(payload.group) && payload.group.length > 0) {
      groupOrSource = payload.group.map(g => g.replace(/\s+/g, '-')).join('_');
    } else if (Array.isArray(payload.source) && payload.source.length > 0) {
      groupOrSource = payload.source.map(g => g.replace(/\s+/g, '-')).join('_');
    }

    const fileName =
      `aguardando-confirmacao-${groupOrSource}-${formatedNow}.xlsx`.toLocaleLowerCase();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error fetching waiting list:', error);
    yield put({
      type: actionTypes.fetchWaitingListError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Indisponível, tente novamente em alguns minutos.',
        variables: { dismissible: true }
      })
    );
  } finally {
    yield put(stopLoading('exportReport'));
  }
}

function* fetchAllGroups() {
  try {
    const { result, success, message } = yield call(getGroups);
    if (success) {
      yield put({
        type: actionTypes.fetchAllGroupsComplete,
        payload: result
      });
    } else {
      yield put(
        createFlashMessage({
          type: 'danger',
          message,
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Error fetching all groups:', e);
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Indisponível, tente novamente em alguns minutos.',
        variables: { dismissible: true }
      })
    );
  }
}

function* fetchAllFAQs({ payload }) {
  try {
    const filter = {
      filter: payload
    };

    if (!filter.filter) {
      filter.filter = {
        pageNumber: 1,
        pageSize: 10,
        search: ''
      };
    }

    const { result, success, message } = yield call(getAllFAQs, filter);

    if (success) {
      yield put({
        type: actionTypes.fetchAllFAQsComplete,
        payload: result
      });
    } else {
      yield put({
        type: actionTypes.fetchAllFAQsError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message,
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Error fetching all FAQs:', e);
    yield put({
      type: actionTypes.fetchAllFAQsError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Erro ao buscar FAQs. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  }
}

function* fetchPublicFAQs(action) {
  try {
    const payload = action?.payload;
    const { result, success } = yield call(getPublicFAQs, payload);

    if (success) {
      yield put({
        type: actionTypes.fetchPublicFAQsComplete,
        payload: result
      });
    } else {
      yield put({
        type: actionTypes.fetchPublicFAQsError
      });
    }
  } catch (e) {
    console.error('Error fetching public FAQs:', e);
    yield put({
      type: actionTypes.fetchPublicFAQsError
    });
  }
}

function* createFAQ({ payload }) {
  try {
    const response = yield call(postCreateFAQ, payload);
    if (response.success) {
      yield put({
        type: actionTypes.createFAQComplete,
        payload: response.result
      });
      yield put(
        createFlashMessage({
          type: 'success',
          message: response.message || 'FAQ criado com sucesso!',
          variables: { dismissible: true }
        })
      );
      yield put({ type: actionTypes.fetchAllFAQs });
    } else {
      yield put({
        type: actionTypes.createFAQError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message: response.message || 'Erro ao criar FAQ.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Error creating FAQ:', e);
    yield put({
      type: actionTypes.createFAQError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Erro ao criar FAQ. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  }
}

function* updateFAQ({ payload }) {
  try {
    const response = yield call(putUpdateFAQ, payload);
    if (response.success) {
      yield put({
        type: actionTypes.updateFAQComplete,
        payload: response.result
      });
      yield put(
        createFlashMessage({
          type: 'success',
          message: 'FAQ atualizado com sucesso!',
          variables: { dismissible: true }
        })
      );
      yield put({ type: actionTypes.fetchAllFAQs });
    } else {
      yield put({
        type: actionTypes.updateFAQError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message: response.message || 'Erro ao atualizar FAQ.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Error updating FAQ:', e);
    yield put({
      type: actionTypes.updateFAQError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Erro ao atualizar FAQ. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  }
}

function* removeFAQ({ payload }) {
  try {
    const response = yield call(deleteFAQ, payload);
    if (response.success) {
      yield put({
        type: actionTypes.removeFAQComplete
      });
      yield put(
        createFlashMessage({
          type: 'success',
          message: 'FAQ deletado com sucesso!',
          variables: { dismissible: true }
        })
      );
      yield put({ type: actionTypes.fetchAllFAQs });
    } else {
      yield put({
        type: actionTypes.removeFAQError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message: response.message || 'Erro ao deletar FAQ.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Error deleting FAQ:', e);
    yield put({
      type: actionTypes.removeFAQError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Erro ao deletar FAQ. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  }
}

function* updateFAQOrder({ payload }) {
  try {
    const { result, success } = yield call(putUpdateFAQOrder, payload);
    if (success) {
      yield put({
        type: actionTypes.updateFAQOrderComplete,
        payload: result
      });
      yield put(
        createFlashMessage({
          type: 'success',
          message: result.message,
          variables: { dismissible: true }
        })
      );
    } else {
      yield put({
        type: actionTypes.updateFAQOrderError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message: result.message || 'Erro ao atualizar ordem das FAQs.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Error updating FAQ order:', e);
    yield put({
      type: actionTypes.updateFAQOrderError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Erro ao atualizar ordem das FAQs. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  }
}

function* enrollStudentSaga({ payload }) {
  const { onSuccess, ...apiPayload } = payload;
  try {
    const response = yield call(putEnrollStudent, apiPayload);
    if (response.success) {
      yield put({
        type: actionTypes.enrollStudentComplete
      });
      yield put(
        createFlashMessage({
          type: 'success',
          message: response.message || 'Aluno admitido na turma com sucesso.',
          variables: { dismissible: true }
        })
      );
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
    } else {
      yield put({
        type: actionTypes.enrollStudentError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message: response.message || 'Erro ao admitir aluno na turma.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Erro no Saga enrollStudent:', e);
    yield put({
      type: actionTypes.enrollStudentError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Ocorreu um erro inesperado. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  }
}

function* markDropoutSaga({ payload }) {
  const { onSuccess, ...apiPayload } = payload;
  try {
    const response = yield call(putMarkDropout, apiPayload);
    if (response.success) {
      yield put({
        type: actionTypes.markDropoutComplete
      });
      yield put(
        createFlashMessage({
          type: 'success',
          message:
            response.message || 'Aluno marcado como desistente com sucesso.',
          variables: { dismissible: true }
        })
      );
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
    } else {
      yield put({
        type: actionTypes.markDropoutError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message: response.message || 'Erro ao marcar aluno como desistente.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Erro no Saga markDropout:', e);
    yield put({
      type: actionTypes.markDropoutError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Ocorreu um erro inesperado. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  }
}

function* fetchClassGroupsByGroupSaga({ payload }) {
  try {
    const group = typeof payload === 'object' ? payload.group : payload;
    const { result, success, message } = yield call(
      getClassGroupsByGroup,
      group
    );
    if (success) {
      yield put({
        type: actionTypes.fetchClassGroupsByGroupComplete,
        payload: result?.classGroupsList || result || []
      });
    } else {
      yield put({
        type: actionTypes.fetchClassGroupsByGroupError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message: message || 'Erro ao buscar turmas.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Erro no Saga fetchClassGroupsByGroup:', e);
    yield put({
      type: actionTypes.fetchClassGroupsByGroupError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Erro ao buscar turmas. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  }
}

function* fetchEnrollmentAuditLogsSaga({ payload }) {
  try {
    const { studentId } = payload;
    const { result, success, message } = yield call(getEnrollmentAuditLogs, {
      studentId
    });
    if (success) {
      yield put({
        type: actionTypes.fetchEnrollmentAuditLogsComplete,
        payload: result?.result || result
      });
    } else {
      yield put({
        type: actionTypes.fetchEnrollmentAuditLogsError
      });
      yield put(
        createFlashMessage({
          type: 'danger',
          message: message || 'Erro ao buscar registros de auditoria.',
          variables: { dismissible: true }
        })
      );
    }
  } catch (e) {
    console.error('Erro no Saga fetchEnrollmentAuditLogs:', e);
    yield put({
      type: actionTypes.fetchEnrollmentAuditLogsError
    });
    yield put(
      createFlashMessage({
        type: 'danger',
        message: 'Erro ao buscar registros de auditoria. Tente novamente.',
        variables: { dismissible: true }
      })
    );
  }
}

export function createAdminSessionSaga(types) {
  return [
    takeLatest(types.fetchAllUsers, fetchAllUsers),
    takeLatest(types.fetchAllPreEnrolled, fetchAllPreEnrolled),
    takeLatest(types.fetchEditUser, fetchEditUser),
    takeLatest(types.fetchAllClassGroups, fetchAllClassGroups),
    takeLatest(types.fetchClassGroupUsers, fetchClassGroupUsers),
    takeLatest(types.newClassGroup, newClassGroup),
    takeLatest(types.editClassGroup, editClassGroup),
    takeLatest(types.removeClassGroup, removeClassGroup),
    takeLatest(types.moveUsersBetweenClassGroups, moveUsersBetweenClassGroups),
    takeLatest(types.fetchAllGroups, fetchAllGroups),
    takeLatest(types.fetchWaitingList, createWaitingListSaga),
    takeLatest(
      types.fetchReprovedAndDropoutList,
      fetchReprovedAndDropoutListSaga
    ),
    takeLatest(types.fetchConfirmedList, fetchConfirmedListSaga),
    takeLatest(types.fetchWaitingToConfirmList, fetchWaitingToConfirmListSaga),
    takeLatest(types.fetchAllGroups, fetchAllGroups),
    takeLatest(types.fetchExportWaitingList, fetchExportWaitingListSaga),
    takeLatest(
      types.fetchExportWaitingToConfirmList,
      fetchExportWaitingToConfirmListSaga
    ),
    takeLatest(types.fetchExportConfirmedList, fetchExportConfirmedListSaga),
    takeLatest(
      types.fetchExportReprovedAndDropoutList,
      fetchExportReprovedAndDropoutListSaga
    ),
    takeLatest(types.fetchAllFAQs, fetchAllFAQs),
    takeLatest(types.fetchPublicFAQs, fetchPublicFAQs),
    takeLatest(types.createFAQ, createFAQ),
    takeLatest(types.updateFAQ, updateFAQ),
    takeLatest(types.removeFAQ, removeFAQ),
    takeLatest(types.updateFAQOrder, updateFAQOrder),
    takeLatest(types.enrollStudent, enrollStudentSaga),
    takeLatest(types.markDropout, markDropoutSaga),
    takeLatest(types.fetchClassGroupsByGroup, fetchClassGroupsByGroupSaga),
    takeEvery(types.fetchEnrollmentAuditLogs, fetchEnrollmentAuditLogsSaga)
  ];
}
